import { formatDateShort, formatMoney } from "../../utils/format"

// Wording for automatic payment approval. A company's bills are only paid from
// the customer's UT wallet by themselves when the customer allows it, so every
// message here says plainly who can take money and when.

const STATUS_TEXT = {
  pending: { label: "Waiting for your choice", tone: "amber" },
  off: { label: "Auto-pay off", tone: "slate" },
  on: { label: "Auto-pay on", tone: "emerald" },
  trusted: { label: "Auto-pay on · you paid them before", tone: "emerald" },
  not_needed: { label: "Your rented unit · always paid", tone: "slate" },
}

export const describeCompanyStatus = (company = {}) =>
  STATUS_TEXT[company.status] || STATUS_TEXT.pending

const billCountText = (count) => `${count} bill${count === 1 ? "" : "s"}`

export const describeWaitingBills = (company = {}) => {
  const count = Number(company.waitingCount || 0)
  if (!(count > 0)) return ""
  return `${billCountText(count)} waiting · ${formatMoney(company.waitingAmount)}`
}

export const describeRequest = (company = {}) =>
  company.requestedAt ? `Asked you on ${formatDateShort(company.requestedAt)}` : ""

export const countWaitingCompanies = (companies = []) =>
  (Array.isArray(companies) ? companies : []).filter((company) => Number(company.waitingCount || 0) > 0).length

// The warning before turning a company on. Turning it on pays the company's
// waiting bills straight away, so the person sees exactly what will happen.
export const buildTurnOnConfirm = (company = {}, walletBalance = 0) => {
  const name = company.providerName || "This company"
  const waiting = Number(company.waitingAmount || 0)
  const wallet = Math.max(0, Number(walletBalance || 0))

  if (!(waiting > 0)) {
    return {
      title: "Turn on auto-pay?",
      message: `${name}'s future bills will be paid from your wallet by themselves. Only do this for a company you know.`,
      details: [{ label: "Company", value: name }],
      confirmLabel: "Turn on",
    }
  }

  const payNow = Math.min(wallet, waiting)
  let message = `Your wallet will pay ${name}'s waiting bills now, oldest first, and their future bills by themselves. Only do this for a company you know.`
  if (payNow < waiting) {
    message += payNow > 0
      ? ` Your wallet covers ${formatMoney(payNow)} now. The rest is paid after you top up.`
      : " Your wallet is empty, so these bills are paid after you top up."
  }

  return {
    title: "Turn on auto-pay and pay now?",
    message,
    details: [
      { label: "Company", value: name },
      { label: "Waiting bills", value: `${billCountText(Number(company.waitingCount || 0))} · ${formatMoney(waiting)}` },
      { label: "Your wallet", value: formatMoney(wallet) },
    ],
    confirmLabel: payNow > 0 ? "Turn on and pay" : "Turn on",
  }
}

// The check before paying one bill by hand.
export const buildPayBillConfirm = (bill = {}, company = {}, walletBalance = 0) => {
  const name = company.providerName || "this company"
  const due = Number(bill.outstandingAmount || 0)
  const wallet = Math.max(0, Number(walletBalance || 0))
  const payNow = Math.min(wallet, due)
  const message = payNow < due
    ? `This pays ${formatMoney(payNow)} of the bill from your wallet. ${formatMoney(due - payNow)} will still be owed.`
    : `This pays this one bill from your wallet. Auto-pay for ${name} stays ${company.approved ? "on" : "off"}.`

  return {
    title: "Pay this bill?",
    message,
    details: [
      { label: "Company", value: company.providerName || "Billing company" },
      { label: "Bill", value: bill.description || "Bill" },
      { label: "Amount due", value: formatMoney(due) },
      { label: "Your wallet", value: formatMoney(wallet) },
    ],
    confirmLabel: `Pay ${formatMoney(payNow)}`,
    payNow,
  }
}

export const describeApprovalResult = ({ company = {}, approved, payment = {} } = {}) => {
  const name = company.providerName || "this company"
  if (!approved) return `Auto-pay is off for ${name}. New bills from them will wait for you.`
  const paid = Number(payment.totalPaid || 0)
  const waiting = Number(company.waitingAmount || 0)
  if (paid > 0 && paid + 0.005 < waiting) {
    return `Auto-pay is on for ${name}. ${formatMoney(paid)} paid now. The rest is paid after you top up.`
  }
  if (paid > 0) return `Auto-pay is on for ${name}. ${formatMoney(paid)} paid from your wallet.`
  if (waiting > 0) return `Auto-pay is on for ${name}. Their bills are paid after you top up.`
  return `Auto-pay is on for ${name}.`
}

export const describePaymentResult = ({ company = {}, result = {} } = {}) => {
  if (result.alreadyPaid) {
    return result.paidByCredit
      ? "This bill was paid from your credit with this company."
      : "This bill is already paid."
  }
  const name = company.providerName || "the company"
  const left = Number(result.outstandingAfter || 0)
  return `Paid ${formatMoney(result.amountPaid)} to ${name}.${left > 0 ? ` ${formatMoney(left)} is still owed.` : ""}`
}

// --- What the billing company sees about one customer ---

const CUSTOMER_CHIPS = {
  pending: { label: "Auto-pay not approved", tone: "amber" },
  off: { label: "Auto-pay turned off", tone: "slate" },
}

export const describeCustomerApprovalChip = (approval) =>
  (approval && CUSTOMER_CHIPS[approval.status]) || null

export const countCustomersAwaitingApproval = (customers = []) =>
  (Array.isArray(customers) ? customers : []).filter((customer) => customer?.autopayApproval?.status === "pending").length

export const describeRetry = (retryAt) =>
  retryAt ? `You can ask again on ${formatDateShort(retryAt)}.` : ""
